import React, { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import {
  useRive,
  Layout,
  Fit,
  Alignment,
} from '@rive-app/react-canvas';
import {
  AssetConfigEntry,
  LoadedGameAssets,
  ImageAsset,
  JsonAsset,
  VideoAsset,
  RiveAsset,
  FontAsset,
  CustomAnimationAsset,
  CustomAnimationSequence,
  CustomAnimationFrame,
  FrameSizeMetadata
} from '../assetConfig';
import { globalAssetsRef } from '../assetStore';
import { DotDotDot } from '~/core/components/DotDotDot';

interface AssetLoaderProps {
  config: AssetConfigEntry[];
  onLoadComplete: (version: number) => void;
  onProgress?: (progress: number) => void;
  onBackgroundProgress?: (progress: number) => void;
}

// Helper to load a single image
const loadImage = (src: string): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    const img = new window.Image();
    img.src = `${location.origin}/${src}`;
    img.onload = () => resolve(img);
    img.onerror = (err) => reject(new Error(`Failed to load image: ${src} - ${err_message(err)}`));
  });
};

// Helper to load a single video
const loadVideo = async (src: string) => {
  const res = await fetch(`${location.origin}/${src}`);
  const blob = await res.blob();
  return URL.createObjectURL(blob);
};

const loadBinary = async (src: string): Promise<ArrayBuffer> => {
  try {
    const response = await fetch(`${location.origin}/${src}`);
    if (!response.ok) {
      throw new Error(`Failed to load binary: ${src} - ${response.status} ${response.statusText}`);
    }
    return await response.arrayBuffer();
  } catch (error) {
    const message = err_message(error);
    throw new Error(`Failed to load binary: ${src} - ${message}`);
  }
};

// Helper to load a single JSON file
const loadJson = (src: string): Promise<any> => {
  return fetch(`${location.origin}/${src}`)
    .then(response => {
      if (!response.ok) {
        throw new Error(`Failed to load JSON: ${src} - ${response.statusText}`);
      }
      return response.json();
    })
    .catch(err => {
        throw new Error(`Failed to load JSON: ${src} - ${err_message(err)}`);
    });
};

// Helper function to get error message from various error types
function err_message(error: any): string {
    if (typeof error === 'string') return error;
    if (error instanceof Error) return error.message;
    if (error instanceof ProgressEvent && error.target) {
        return `Network error with target ${ (error.target as EventTarget & {src?:string})?.src || 'unknown'}`;
    }
    return 'Unknown error';
}

// Helper to create a unique key for an image path
const imagePathToKey = (path: string): string => {
  return path.replace(/[^a-zA-Z0-9]/g, '_'); // Replace non-alphanumeric with underscore
};

type FrameSizeRaw = {
  original_size?: [number, number];
  cropped_size?: [number, number];
  bbox?: [number, number, number, number];
};

type AnimationFrameDefinition =
  | string
  | (Record<string, unknown> & {
      src?: string;
      path?: string;
      image?: string;
      file?: string;
      size?: FrameSizeRaw;
    });

const extractFrameImagePath = (frame: AnimationFrameDefinition): string | null => {
  if (typeof frame === 'string') {
    return frame;
  }
  if (frame && typeof frame === 'object') {
    return (frame.path ?? frame.src ?? frame.image ?? frame.file ?? null) as string | null;
  }
  return null;
};

const parseSizeBlock = (sizeBlock?: FrameSizeRaw): FrameSizeMetadata | undefined => {
  if (!sizeBlock) {
    return undefined;
  }
  const { original_size, cropped_size, bbox } = sizeBlock;
  if (
    !Array.isArray(original_size) ||
    original_size.length !== 2 ||
    !Array.isArray(cropped_size) ||
    cropped_size.length !== 2 ||
    !Array.isArray(bbox) ||
    bbox.length !== 4
  ) {
    console.warn('Invalid frame size metadata detected, ignoring size information.', sizeBlock);
    return undefined;
  }

  const originalSize: [number, number] = [Number(original_size[0]), Number(original_size[1])];
  const croppedSize: [number, number] = [Number(cropped_size[0]), Number(cropped_size[1])];
  const bboxValues: [number, number, number, number] = [
    Number(bbox[0]),
    Number(bbox[1]),
    Number(bbox[2]),
    Number(bbox[3])
  ];

  if (originalSize.some(isNaN) || croppedSize.some(isNaN) || bboxValues.some(isNaN)) {
    console.warn('Frame size metadata contains non-numeric values, ignoring.', sizeBlock);
    return undefined;
  }

  return {
    originalSize,
    croppedSize,
    bbox: bboxValues,
  };
};

const parseFrameSizeMetadata = (frame: AnimationFrameDefinition): FrameSizeMetadata | undefined => {
  if (!frame || typeof frame !== 'object') {
    return undefined;
  }
  return parseSizeBlock((frame as { size?: FrameSizeRaw }).size);
};

const REQUIRED_CONCURRENCY = 8;
const BACKGROUND_CONCURRENCY = 4;
const FRAME_CONCURRENCY = 6;

type ProgressTracker = {
  ref: React.MutableRefObject<{ completed: number; total: number }>;
  callback?: (progress: number) => void;
};

const runWithConcurrency = async (tasks: Array<() => Promise<void>>, concurrency: number) => {
  if (tasks.length === 0) return;
  let cursor = 0;
  const worker = async () => {
    while (cursor < tasks.length) {
      const current = tasks[cursor];
      cursor += 1;
      try {
        await current();
      } catch {
        // Individual tasks already handle their own errors; keep draining queue
      }
    }
  };
  const runners = Array.from(
    { length: Math.min(concurrency, tasks.length) },
    () => worker()
  );
  await Promise.all(runners);
};

interface AnimationDefinitionJson {
  frames?: AnimationFrameDefinition[];
  loop?: boolean | number[];
  frameRate?: number;
  frameWidth?: number;
  frameHeight?: number;
  anchor?: { x: number; y: number };
  size?: FrameSizeRaw;
  events?: unknown;
}

interface AnimationJsonFile {
  animations?: Record<string, AnimationDefinitionJson>;
  size?: FrameSizeRaw;
  framerate?: number;
  anchor?: { x: number; y: number };
  frameWidth?: number;
  frameHeight?: number;
}

const clampProgress = (value: number) => Math.max(0, Math.min(100, value));

export const BeeLoadingVisual: React.FC<{ progress: number; wrongPwd?: boolean }> = ({ progress, wrongPwd }) => {
  const { RiveComponent } = useRive({
    src: '/blinko/loading.riv',
    autoplay: true,
    layout: new Layout({
      fit: Fit.Contain,
      alignment: Alignment.Center,
    }),
  });

  const safeProgress = clampProgress(progress);

  const riveAnimation = (
    <div className="relative flex h-[300px] md:h-[400px] aspect-square items-center justify-center overflow-hidden -mt-[80px] -mb-[80px] md:-mt-[100px] md:-mb-[100px]">
      <RiveComponent className="w-full h-full" />
    </div>
  );

  if (wrongPwd) {
    return (
      <div className="relative flex w-full max-w-xl flex-col items-center gap-8 px-8">
        {riveAnimation}
        <div className="w-full space-y-2">
          <div className=''>
            <DotDotDot>
              {addDots => (
                <span className='relative font-blinko text-xs opacity-50'>BEE BACK SOON</span>
              )}
            </DotDotDot>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="relative flex w-full max-w-xl flex-col items-center gap-8 px-8">
      {riveAnimation}
      <div className="w-[180px] md:w-[220px] space-y-2">
        <div
          className="relative h-1.5 md:h-3 w-full overflow-hidden rounded-full border border-white/5 bg-white/10"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={safeProgress}
        >
         <motion.div
            className="h-full w-full origin-left bg-gradient-to-r from-amber-200 via-orange-300 to-amber-500 shadow-[0_0_30px_rgba(255,188,65,0.4)]"
            initial={{ scaleX: 0 }}
            animate={{ scaleX: safeProgress / 100 }}
            transition={{ type: 'spring', stiffness: 160, damping: 22 }}
          />
        </div>
        <div className=''>
          <DotDotDot>
            {addDots => (
              <span className='relative font-blinko text-xs opacity-50'>LOADING<span className='absolute left-full'>{addDots()}</span></span>
            )}
          </DotDotDot>
        </div>
      </div>
    </div>
  );
};

const AssetLoader: React.FC<AssetLoaderProps> = ({ config, onLoadComplete, onProgress, onBackgroundProgress }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const assetsRef = globalAssetsRef;
  const isMountedRef = useRef(true);
  const progressRef = useRef({ completed: 0, total: 0 });
  const backgroundProgressRef = useRef({ completed: 0, total: 0 });
  const versionRef = useRef(0);

  const computeAndReport = (tracker: ProgressTracker | null) => {
    if (!tracker) return;
    const { completed, total } = tracker.ref.current;
    const progress = total > 0 ? Math.round((completed / total) * 100) : 0;
    tracker.callback?.(progress);
  };

  const updateProgress = () => computeAndReport({ ref: progressRef, callback: onProgress });
  const updateBackgroundProgress = () => computeAndReport({ ref: backgroundProgressRef, callback: onBackgroundProgress });

  useEffect(() => {
    isMountedRef.current = true;
    assetsRef.current = { videos: {}, images: {}, jsonData: {}, riveFiles: {}, fonts: {}, customAnimations: {} };
    let encounteredError = false;

    const getPriority = (asset: AssetConfigEntry) => {
      if ('priority' in asset && asset.priority) return asset.priority;
      return 'required';
    };

    const blockingAssets = config.filter(asset => getPriority(asset) !== 'background');
    const backgroundAssets = config.filter(asset => getPriority(asset) === 'background');
    const deferredAnimationLoads: CustomAnimationAsset[] = [];

    blockingAssets.forEach(asset => {
      if (asset.type === 'custom_animation') {
        const ca = asset as CustomAnimationAsset;
        if (ca.eagerAnimations?.length && ca.loadRestInBackground !== false) {
          deferredAnimationLoads.push(ca);
        }
      }
    });

    const requiredTracker: ProgressTracker = { ref: progressRef, callback: onProgress };
    const backgroundTracker: ProgressTracker = { ref: backgroundProgressRef, callback: onBackgroundProgress };

    progressRef.current = { completed: 0, total: blockingAssets.length };
    updateProgress();
    backgroundProgressRef.current = { completed: 0, total: 0 };

    const bumpVersion = (meaningful: boolean = true) => {
      if (!meaningful) return;
      versionRef.current += 1;
    };

    const recordError = (message: string) => {
      encounteredError = true;
      setError(prev => prev ? `${prev}\n${message}` : message);
    };

    const bumpProgress = (tracker: ProgressTracker | null, delta: number = 1) => {
      if (!tracker) return;
      tracker.ref.current.completed += delta;
      computeAndReport(tracker);
    };

    const growTotal = (tracker: ProgressTracker | null, delta: number) => {
      if (!tracker) return;
      tracker.ref.current.total += delta;
      computeAndReport(tracker);
    };

    const loadCustomAnimation = (
      caAsset: CustomAnimationAsset,
      options: { tracker?: ProgressTracker | null; animationsToLoad?: Set<string>; skipAlreadyLoaded?: boolean }
    ): Promise<void> => {
      const { tracker = null, animationsToLoad, skipAlreadyLoaded = true } = options;

      return loadJson(caAsset.jsonSrc)
        .then((animationJsonData: AnimationJsonFile) => {
          if (!isMountedRef.current) return;
          assetsRef.current.jsonData[caAsset.key] = animationJsonData;

          const existingAnimations = assetsRef.current.customAnimations[caAsset.key] || {};
          const animations = animationJsonData.animations || {};
          const entriesToProcess = Object.entries(animations).filter(([animName]) => {
            if (animationsToLoad && !animationsToLoad.has(animName)) return false;
            const existing = existingAnimations[animName];
            if (skipAlreadyLoaded && existing && Array.isArray(existing.frames) && existing.frames.length > 0) {
              return false;
            }
            return true;
          });

          const frameTasks: Array<() => Promise<void>> = [];
          const loadedFrameImageKeys: Record<string, string> = {};
          const uniqueFrameImagePaths = new Set<string>();

          entriesToProcess.forEach(([, animDef]) => {
            if (Array.isArray(animDef.frames)) {
              animDef.frames.forEach(frameDefinition => {
                const frameImagePath = extractFrameImagePath(frameDefinition);
                if (frameImagePath) {
                  uniqueFrameImagePaths.add(frameImagePath);
                }
              });
            }
          });

          const frameCount = uniqueFrameImagePaths.size;
          growTotal(tracker, frameCount);

          uniqueFrameImagePaths.forEach(frameImagePath => {
            const imageKey = imagePathToKey(frameImagePath);
            loadedFrameImageKeys[frameImagePath] = imageKey;
            if (skipAlreadyLoaded && assetsRef.current.images[imageKey]) {
              return;
            }
            frameTasks.push(() =>
              loadImage(frameImagePath)
                .then(img => {
                  if (isMountedRef.current) {
                    assetsRef.current.images[imageKey] = img;
                    bumpProgress(tracker, 1);
                    bumpVersion(false);
                  }
                })
                .catch(err => {
                  if (isMountedRef.current) {
                    recordError(err_message(err));
                    bumpProgress(tracker, 1);
                  }
                  console.error(`Failed to load custom animation frame ${frameImagePath}:`, err);
                })
            );
          });

          const rootSizeMetadata = parseSizeBlock(animationJsonData.size);

          return runWithConcurrency(frameTasks, FRAME_CONCURRENCY).then(() => {
            if (!isMountedRef.current) return;
            if (!entriesToProcess.length) {
              bumpProgress(tracker, 1);
              return;
            }

            const processedAnimations: Record<string, CustomAnimationSequence> = { ...existingAnimations };

            const commonFrameRate = animationJsonData.framerate || 30;
            const commonAnchor = animationJsonData.anchor || { x: 0.5, y: 0.5 };
            const commonFrameWidth = animationJsonData.frameWidth || 50;
            const commonFrameHeight = animationJsonData.frameHeight || 50;

            entriesToProcess.forEach(([animName, animDef]) => {
              const animationLevelSize = parseSizeBlock(animDef.size) || rootSizeMetadata;

              const frames: CustomAnimationFrame[] = (animDef.frames || []).map((frameDefinition, idx) => {
                const frameImagePath = extractFrameImagePath(frameDefinition);
                if (!frameImagePath) {
                  console.warn(`Invalid or missing frame image path at index ${idx} in animation ${animName}`, frameDefinition);
                  return null;
                }
                const key = loadedFrameImageKeys[frameImagePath];
                if (!key) {
                  console.warn(`Frame image not preloaded: ${frameImagePath} in animation ${animName}`);
                  return null;
                }

                const frameSize = parseFrameSizeMetadata(frameDefinition) || animationLevelSize;
                const frame: CustomAnimationFrame = { imageKey: key };
                if (frameSize) {
                  frame.size = frameSize;
                }

                if (frameDefinition && typeof frameDefinition === 'object') {
                  const frameObj = frameDefinition as Record<string, unknown> & {
                    events?: unknown;
                    event?: unknown;
                  };
                  let events: string[] | undefined;
                  if (Array.isArray(frameObj.events)) {
                    events = frameObj.events.filter((e): e is string => typeof e === 'string');
                  } else if (typeof frameObj.event === 'string') {
                    events = [frameObj.event];
                  }
                  if (events && events.length > 0) {
                    frame.events = events;
                  }
                }

                return frame;
              }).filter((frame): frame is CustomAnimationFrame => frame !== null);
              
              // Parse optional per-frame events if present in JSON (sequence-level)
              let frameEvents: Record<number, string[]> | undefined = undefined;
              if (animDef && animDef.events) {
                const ev = animDef.events;
                frameEvents = {};
                if (Array.isArray(ev)) {
                  // Support array of { frame, name } objects
                  ev.forEach((e: any) => {
                    const f = typeof e?.frame === 'number' ? e.frame : parseInt(e?.frame);
                    const name = e?.name;
                    if (!isNaN(f) && typeof name === 'string') {
                      if (!frameEvents![f]) frameEvents![f] = [];
                      frameEvents![f].push(name);
                    }
                  });
                } else if (typeof ev === 'object') {
                  // Support map of frameIndex -> eventName or eventName[]
                  Object.keys(ev as Record<string, unknown>).forEach(k => {
                    const f = parseInt(k);
                    if (isNaN(f)) return;
                    const val = (ev as Record<string, unknown>)[k];
                    if (typeof val === 'string') {
                      frameEvents![f] = [val];
                    } else if (Array.isArray(val)) {
                      const filtered = val.filter((x): x is string => typeof x === 'string');
                      if (filtered.length > 0) {
                        frameEvents![f] = filtered;
                      }
                    }
                  });
                }
                // Clean empty
                if (frameEvents && Object.keys(frameEvents).length === 0) {
                  frameEvents = undefined;
                }
              }

              processedAnimations[animName] = {
                frames,
                frameRate: animDef.frameRate || commonFrameRate, 
                loop: animDef.loop !== undefined ? animDef.loop : true,
                frameWidth: animDef.frameWidth || commonFrameWidth, 
                frameHeight: animDef.frameHeight || commonFrameHeight, 
                anchor: animDef.anchor || commonAnchor,
                frameEvents,
              };
            });

            // If we're loading a subset (eager), register stub entries for the rest
            if (animationsToLoad) {
              Object.keys(animationJsonData.animations || {}).forEach(name => {
                if (processedAnimations[name]) return;
                processedAnimations[name] = {
                  frames: [],
                  frameRate: commonFrameRate,
                  loop: true,
                  frameWidth: commonFrameWidth,
                  frameHeight: commonFrameHeight,
                  anchor: commonAnchor,
                };
              });
            }

            assetsRef.current.customAnimations[caAsset.key] = processedAnimations;
            bumpProgress(tracker, 1);
            bumpVersion();
          });
        })
        .catch(err => {
          if (isMountedRef.current) {
            recordError(err_message(err));
            bumpProgress(tracker, 1);
          }
          console.error(err);
        });
    };

    const loadAsset = (
      asset: AssetConfigEntry,
      options: { tracker?: ProgressTracker | null; animationsToLoad?: Set<string> }
    ) => {
      const { tracker = null, animationsToLoad } = options;
      if (asset.type === 'image') {
        const imageAsset = asset as ImageAsset;
        return loadImage(imageAsset.src)
          .then(img => {
            if (isMountedRef.current) {
            assetsRef.current.images[imageAsset.key] = img;
            bumpProgress(tracker, 1);
            bumpVersion(false);
            }
          })
          .catch(err => {
            if (isMountedRef.current) {
              recordError(err_message(err));
              bumpProgress(tracker, 1);
            }
            console.error(err);
          });
      } else if (asset.type === 'video') {
        const videoAsset = asset as VideoAsset;
        return loadVideo(videoAsset.src)
          .then(video => {
            if (isMountedRef.current) {
            assetsRef.current.videos[videoAsset.key] = video;
            bumpProgress(tracker, 1);
            bumpVersion(false);
            }
          })
          .catch(err => {
            if (isMountedRef.current) {
              recordError(err_message(err));
              bumpProgress(tracker, 1);
            }
            console.error(err);
          });
      } else if (asset.type === 'json') {
        const jsonAsset = asset as JsonAsset;
        return loadJson(jsonAsset.src)
          .then(data => {
            if (isMountedRef.current) {
            assetsRef.current.jsonData[jsonAsset.key] = data;
            bumpProgress(tracker, 1);
            bumpVersion(false);
            }
          })
          .catch(err => {
            if (isMountedRef.current) {
              recordError(err_message(err));
              bumpProgress(tracker, 1);
            }
            console.error(err);
          });
      } else if (asset.type === 'font') {
        const fontAsset = asset as FontAsset;
        return loadBinary(fontAsset.src)
          .then(buffer => {
            if (isMountedRef.current) {
            assetsRef.current.fonts[fontAsset.key] = buffer;
            bumpProgress(tracker, 1);
            bumpVersion(false);
            }
          })
          .catch(err => {
            if (isMountedRef.current) {
              recordError(err_message(err));
              bumpProgress(tracker, 1);
            }
            console.error(err);
          });
      } else if (asset.type === 'rive') {
        const riveAsset = asset as RiveAsset;
        return loadBinary(riveAsset.src)
          .then(buffer => {
            if (isMountedRef.current) {
            assetsRef.current.riveFiles[riveAsset.key] = buffer;
            bumpProgress(tracker, 1);
            bumpVersion(false);
            }
          })
          .catch(err => {
            if (isMountedRef.current) {
              recordError(err_message(err));
              bumpProgress(tracker, 1);
            }
            console.error(err);
          });
      } else if (asset.type === 'custom_animation') {
        return loadCustomAnimation(asset as CustomAnimationAsset, { tracker, animationsToLoad, skipAlreadyLoaded: true });
      }
      return Promise.resolve();
    };

    const requiredTasks = blockingAssets.map(asset => {
      if (asset.type === 'custom_animation') {
        const caAsset = asset as CustomAnimationAsset;
        const eagerSet = caAsset.eagerAnimations?.length ? new Set(caAsset.eagerAnimations) : undefined;
        return () => loadAsset(caAsset, { tracker: requiredTracker, animationsToLoad: eagerSet });
      }
      return () => loadAsset(asset, { tracker: requiredTracker });
    });

    runWithConcurrency(requiredTasks, REQUIRED_CONCURRENCY)
      .then(() => {
        if (isMountedRef.current && !encounteredError) { 
          bumpVersion();
          onLoadComplete(versionRef.current);
          setIsLoading(false);
        } else if (isMountedRef.current && encounteredError) {
          setIsLoading(false); 
        }

        const backgroundTasks: Array<() => Promise<void>> = [];

        const enqueueBackground = (asset: AssetConfigEntry) => {
          backgroundTasks.push(() => loadAsset(asset, { tracker: backgroundTracker }));
        };

        backgroundAssets.forEach(enqueueBackground);
        deferredAnimationLoads.forEach(asset => {
          backgroundTasks.push(() => loadCustomAnimation(asset, { tracker: backgroundTracker, skipAlreadyLoaded: true }));
        });

        if (backgroundTasks.length) {
          backgroundProgressRef.current = { completed: 0, total: backgroundTasks.length };
          updateBackgroundProgress();
          runWithConcurrency(backgroundTasks, BACKGROUND_CONCURRENCY)
            .then(() => {
              if (isMountedRef.current) {
                bumpVersion();
                onLoadComplete(versionRef.current);
                backgroundProgressRef.current.completed = backgroundProgressRef.current.total;
                updateBackgroundProgress();
              }
            })
            .catch(overallError => {
              if (isMountedRef.current) {
                recordError(`Overall loading error: ${err_message(overallError)}`);
              }
              console.error("Overall asset loading error:", overallError);
            });
        }
      })
      .catch(overallError => {
        if (isMountedRef.current) {
          const message = err_message(overallError);
          recordError(`Overall loading error: ${message}`);
          setIsLoading(false);
          console.error("Overall asset loading error:", overallError);
        }
      });
    
    return () => {
      isMountedRef.current = false;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); 

  if (error) {
    return <div style={{ color: 'red', padding: '20px', whiteSpace: 'pre-wrap' }}>Error loading assets:\n{error}</div>;
  }

  return null; 
};

export default AssetLoader; 
